import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, TriangleAlert as AlertTriangle } from 'lucide-react';
import AccountDeletionSuccessModal from '../AccountDeletionSuccessModal/AccountDeletionSuccessModal';
import { trackAuth, trackError } from '../Utils/analytics';
import { clearAuthData, authenticatedFetch } from '../Utils/apiHelpers';
import { DELETE_USER_URL } from '../../config/api';
import './UserAccountDeletion.css';

interface UserAccountDeletionProps {
  onClose?: () => void;
  onLogout?: () => void;
}

const UserAccountDeletion: React.FC<UserAccountDeletionProps> = ({ onClose, onLogout }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAccountDeletionSuccess, setShowAccountDeletionSuccess] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    setError(null);

    try {
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        setError('Authentication required. Please log in and try again.');
        setIsDeletingAccount(false);
        return;
      }

      const response = await authenticatedFetch(DELETE_USER_URL, {
        method: 'DELETE',
        headers: { accept: 'application/json' },
      });

      if (response.ok) {
        clearAuthData();
        setShowDeleteConfirmation(false);
        setShowAccountDeletionSuccess(true);
        trackAuth('account_deleted');
      } else {
        const errorData = await response.json();
        if (response.status === 404) {
          setError('User account not found or already deleted.');
        } else if (response.status === 500) {
          setError('Server error occurred while deleting account. Please try again later.');
        } else {
          setError(errorData.message || 'Failed to delete account. Please try again.');
        }
        trackError('account_deletion_failed', 'user_account_deletion');
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      setError('Network error occurred while deleting account. Please check your connection.');
      trackError('account_deletion_network_error', 'user_account_deletion');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleAccountDeletionSuccess = () => {
    if (onLogout) onLogout();
    navigate('/', { replace: true });
  };

  // Show account deletion success modal
  if (showAccountDeletionSuccess) {
    return (
      <AccountDeletionSuccessModal 
        onContinue={handleAccountDeletionSuccess}
      />
    );
  }

  return (
    <div className="user-account-deletion-page">
      {/* Header */}
      <div className="user-account-deletion-header">
        <h3 className="user-account-deletion-title">Account Deletion</h3>
        <p className="user-account-deletion-subtitle">
          Permanently remove your account and all associated data
        </p>
      </div>

      {/* Delete Account Zone */}
      <div className="user-account-deletion-zone">
        <div className="user-account-deletion-zone-header">
          <div className="user-account-deletion-icon">
            <Trash2 size={24} />
          </div>
          <h4 className="user-account-deletion-zone-title">Delete Account</h4>
        </div>
        
        <p className="user-account-deletion-description">
          Permanently delete your account and all associated data. This action cannot be undone and will remove all your information from our systems.
        </p>
        
        <div className="user-account-deletion-warning-box">
          <AlertTriangle size={24} />
          <p className="user-account-deletion-warning-text">
            This action is permanent and irreversible. All your data will be permanently deleted.
          </p>
        </div>

        {error && (
          <div className="user-account-deletion-error">
            {error}
          </div>
        )}
        
        <div className="user-account-deletion-actions">
          <button 
            onClick={() => setShowDeleteConfirmation(true)}
            disabled={isDeletingAccount}
            className="user-account-deletion-btn"
          >
            {isDeletingAccount ? (
              <>
                <div className="user-account-deletion-spinner"></div>
                Deleting Account...
              </>
            ) : (
              <>
                <Trash2 size={20} />
                Delete My Account
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="user-account-deletion-confirm-overlay">
          <div className="user-account-deletion-modal">
            <div className="user-account-deletion-modal-header">
              <div className="user-account-deletion-modal-icon">
                <AlertTriangle size={32} />
              </div>
              <h3 className="user-account-deletion-modal-title">Confirm Account Deletion</h3>
            </div>

            <div className="user-account-deletion-modal-content">
              <p className="user-account-deletion-modal-text">
                Are you absolutely sure you want to delete your account? This action is permanent and cannot be undone.
              </p>

              <div className="user-account-deletion-consequences-list">
                <h4 className="user-account-deletion-consequences-title">What will be deleted:</h4>
                <div className="user-account-deletion-consequences-items">
                  <div className="user-account-deletion-consequence-item">Profile information</div>
                  <div className="user-account-deletion-consequence-item">Order history</div>
                  <div className="user-account-deletion-consequence-item">Kitchen data and all food listings</div>
                  <div className="user-account-deletion-consequence-item">Payment methods</div>
                  <div className="user-account-deletion-consequence-item">Reviews & ratings</div>
                  <div className="user-account-deletion-consequence-item">All personal data</div>
                </div>
              </div>

              <div className="user-account-deletion-extra-warnings">
                <p>Any pending or active orders may be cancelled.</p>
                <p>If you have a registered kitchen, it will be permanently removed along with all food listings.</p>
                <p>You will need to create a new account to use HomeCookt again.</p>
              </div>

              <div className="user-account-deletion-final-warning">
                <p className="user-account-deletion-final-warning-text">
                  This action is PERMANENT and IRREVERSIBLE
                </p>
              </div>

              <div className="user-account-deletion-confirm-input">
                <label htmlFor="delete-confirmation">
                  Type <strong>DELETE</strong> to confirm:
                </label>
                <input
                  id="delete-confirmation"
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type DELETE here"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="user-account-deletion-modal-actions">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setConfirmationText('');
                }}
                className="user-account-deletion-cancel-btn"
                disabled={isDeletingAccount}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount || confirmationText.toUpperCase() !== 'DELETE'}
                className="user-account-deletion-confirm-btn"
              >
                {isDeletingAccount ? (
                  <>
                    <div className="user-account-deletion-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    Delete Forever
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccountDeletion;